// Tamil-Katral-Maiyam\src\pages\teacher\Profile.tsx

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Pencil, Save, X } from 'lucide-react';
import {
  getTeacherProfile,
  updateTeacherProfile,
  TeacherProfilePayload,
} from '../../modules/roster/api/teacher.client';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { sanitizePhone, PHONE_MAX_CHARS } from '../../utils/validation';
import { Spinner } from '../../components/ui/Spinner';
interface TeacherFormData {
  display_name: string;
  first_name: string;
  last_name: string;
  phone?: string;
  notes?: string;
}

export const TeacherProfile: React.FC = () => {
  const [teacher, setTeacher] = useState<TeacherProfilePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, watch, setValue } = useForm<TeacherFormData>({
    defaultValues: {
      display_name: '',
      first_name: '',
      last_name: '',
      phone: '',
      notes: '',
    },
  });

  const displayNameVal = (watch('display_name') || '').trim();
  const firstNameVal = (watch('first_name') || '').trim();
  const lastNameVal = (watch('last_name') || '').trim();
  const phoneVal = (watch('phone') || '').trim();
  const notesVal = (watch('notes') || '').trim();

  const placeholders: Record<keyof TeacherFormData, string> = {
    display_name: displayNameVal ? displayNameVal : 'Enter your name',
    first_name: firstNameVal ? firstNameVal : 'Enter your first name',
    last_name: lastNameVal ? lastNameVal : 'Enter your last name',
    phone: phoneVal ? phoneVal : 'Enter your phone number',
    notes: notesVal ? notesVal : 'Write notes about you (optional)',
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await getTeacherProfile();
      setTeacher(data);
      reset({
        display_name: data.display_name || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone: data.phone || '',
        notes: data.notes || '',
      });
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleCancel = () => {
    if (teacher) {
      reset({
        display_name: teacher.display_name || '',
        first_name: teacher.first_name || '',
        last_name: teacher.last_name || '',
        phone: teacher.phone || '',
        notes: teacher.notes || '',
      });
    }
    setIsEditing(false);
  };

  const onSubmit = async (values: TeacherFormData) => {
    if (!teacher) return;
    setIsSaving(true);
    try {
      await updateTeacherProfile({
        display_name: values.display_name,
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone || undefined,
        notes: values.notes,
      });
      showSuccessToast('Profile updated');
      await loadProfile();
      setIsEditing(false);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 w-full">
        <div className="mx-auto w-full max-w-5xl px-6 py-8 space-y-6">
          <PageHeader
            title="My Profile"
            subtitle="Preview and edit your teacher details"
            actions={
              isEditing ? (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isSaving}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isSaving}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )
            }
          />

          {/* <Card className="w-full">
            <CardHeader>
              <CardTitle>Account details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Teacher ID
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">{teacher?.id || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Tenant
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">{teacher?.tenant_id || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Platform ID
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">{teacher?.platform_user_id || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Status
                  </p>
                  <Badge variant={teacher?.status === 'active' ? 'success' : 'gray'}>
                    {teacher?.status || '-'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Created
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {teacher?.created_at ? teacher.created_at : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Updated
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {teacher?.updated_at ? teacher.updated_at : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card> */}

          <Card className="w-full">
            <CardHeader>
              <CardTitle>School-managed details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email and employment are updated by your school admin, not from this page.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    Email
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">{teacher?.email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    Employee code
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {teacher?.employment?.employee_code || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    Joining date
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {teacher?.employment?.joining_date || '—'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="w-full">
            <CardHeader>
              <CardTitle>Your profile</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4">
                <Input
                  label="Display Name"
                  placeholder={placeholders.display_name}
                  {...register('display_name', { required: true })}
                  disabled={!isEditing}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    placeholder={placeholders.first_name}
                    {...register('first_name')}
                    disabled={!isEditing}
                  />
                  <Input
                    label="Last Name"
                    placeholder={placeholders.last_name}
                    {...register('last_name')}
                    disabled={!isEditing}
                  />
                </div>

                <Input
                  label="Phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={PHONE_MAX_CHARS}
                  placeholder={placeholders.phone}
                  {...register('phone', {
                    onChange: (e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true }),
                  })}
                  disabled={!isEditing}
                />

                <Textarea
                  label="Notes"
                  placeholder={placeholders.notes}
                  {...register('notes')}
                  disabled={!isEditing}
                  rows={3}
                />
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
  